import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    Button,
    Tooltip,
    Snackbar,
    Alert,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFilePdf,
    faFileWord,
    faFileImage,
    faFile,
    faTrash,
    faDownload,
    faRotateRight,
    faCircleExclamation,
    faShieldHalved,
    faUpload,
    faPlus,
    faTriangleExclamation,
    faChevronRight,
    faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
    documentsApi,
    DocumentListItem,
    DocumentPageInfo,
    DocumentStatus,
    DocumentStatusResponse,
} from '../api/documents';
import { workItemsApi } from '../api/workItems';
import ConfirmationDialog from './ConfirmationDialog';
import { supabase } from '../lib/supabaseClient';
import {
    validateFile,
    getApiErrorMessage,
    humanizeError,
    getHintDomain,
} from '../lib/documentValidation';
import DocumentsAnalyticalPanel from './DocumentsAnalyticalPanel';
import IntakeDecisionGuideCard from './decisionGuide/IntakeDecisionGuideCard';
import { DECISION_GUIDE_LIST_FAILED_BODY, DECISION_GUIDE_LIST_FAILED_TITLE } from './decisionGuide/decisionGuideCopy';

interface DocumentsHistoryProps {
    refreshTrigger: number;
    /** From bootstrap entitlements; gates new deep analysis / Q&A, not read access to saved results. */
    analyticalEnabled?: boolean;
    /** After image document jobs reach a terminal server state, refresh entitlements (finalize_ai_credit_reservation success path). */
    onEntitlementsRefresh?: () => void;
}

const DOCS_STALE_MS = 60_000;
const POLL_INTERVAL_MS = 4_000;
const docsPageCache = new Map<number, { items: DocumentListItem[]; hasMore: boolean; savedAt: number }>();

function getCachedDocsPage(page: number) {
    const entry = docsPageCache.get(page);
    if (!entry) return null;
    if (Date.now() - entry.savedAt > 5 * 60_000) {
        docsPageCache.delete(page);
        return null;
    }
    return entry;
}

function setCachedDocsPage(page: number, items: DocumentListItem[], hasMore: boolean) {
    docsPageCache.set(page, { items, hasMore, savedAt: Date.now() });
}

type PersistedStatusSnapshot = DocumentStatusResponse & { status: DocumentStatus };
type PollIssue = { kind: 'transient' | 'auth' | 'missing'; message: string };

const normalizeDocumentStatus = (status: string | null | undefined): DocumentStatus => {
    const normalized = (status || '').trim().toLowerCase();
    switch (normalized) {
        case 'completed':
            return 'processed';
        case 'pending':
            return 'processing';
        case 'uploaded':
        case 'processing':
        case 'processed':
        case 'partial':
        case 'failed':
        case 'deleted':
        case 'missing':
        case 'unknown':
            return normalized as DocumentStatus;
        default:
            return 'unknown';
    }
};

const isTerminalStatus = (status: string | null | undefined): boolean => {
    const normalized = normalizeDocumentStatus(status);
    return normalized === 'processed' || normalized === 'partial' || normalized === 'failed';
};

const shouldPollStatus = (status: string | null | undefined): boolean => {
    const normalized = normalizeDocumentStatus(status);
    return normalized === 'uploaded' || normalized === 'processing';
};

const getActionPayload = (doc: DocumentListItem) => ({
    intent: doc.doc_type || 'general',
    hint_domain: getHintDomain(doc.doc_type),
});

const getPollingIssueMessage = (error: any): PollIssue => {
    const statusCode = error?.response?.status;
    if (statusCode === 401) {
        return {
            kind: 'auth',
            message: 'We could not refresh this status because your session needs attention.',
        };
    }
    if (statusCode === 404) {
        return {
            kind: 'missing',
            message: 'We could not find this document anymore. Try refreshing the page if this looks wrong.',
        };
    }
    return {
        kind: 'transient',
        message: 'We could not refresh this status right now. You can try again manually.',
    };
};

const getActionErrorMessage = (error: any): string => {
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail === 'object' && detail.code === 'AI_CREDITS_EXHAUSTED') {
        return 'Your file is safe. AI analysis is paused until your credits reset.';
    }
    if (detail && typeof detail === 'object' && detail.code === 'ANALYTICAL_NOT_AVAILABLE') {
        return typeof detail.message === 'string' ? detail.message : 'Deep analysis is not included with your account.';
    }
    return humanizeError(getApiErrorMessage(error));
};

const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return <FontAwesomeIcon icon={faFilePdf} style={{ color: '#ef4444' }} />;
    if (mimeType.includes('word') || mimeType.includes('officedocument')) return <FontAwesomeIcon icon={faFileWord} style={{ color: '#3b82f6' }} />;
    if (mimeType.includes('image')) return <FontAwesomeIcon icon={faFileImage} style={{ color: '#10b981' }} />;
    return <FontAwesomeIcon icon={faFile} style={{ color: '#9ca3af' }} />;
};

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentsHistory: React.FC<DocumentsHistoryProps> = ({
    refreshTrigger,
    analyticalEnabled = false,
    onEntitlementsRefresh,
}) => {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState<DocumentListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [reuploading, setReuploading] = useState(false);
    const [pendingReuploadDoc, setPendingReuploadDoc] = useState<DocumentListItem | null>(null);
    const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Delete confirmation state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [pendingDeleteDoc, setPendingDeleteDoc] = useState<DocumentListItem | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [creatingWorkItem, setCreatingWorkItem] = useState(false);
    const [listFetchError, setListFetchError] = useState<string | null>(null);
    const [statusSnapshots, setStatusSnapshots] = useState<Record<string, PersistedStatusSnapshot>>({});
    const [pollIssues, setPollIssues] = useState<Record<string, PollIssue>>({});
    const activePollRequestsRef = useRef<Set<string>>(new Set());
    const suppressedPollsRef = useRef<Set<string>>(new Set());
    const primedPollsRef = useRef<Set<string>>(new Set());
    const isMountedRef = useRef(true);
    const documentsRef = useRef<DocumentListItem[]>([]);

    useEffect(() => {
        documentsRef.current = documents;
    }, [documents]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const updateDocument = useCallback((documentId: string, updater: (doc: DocumentListItem) => DocumentListItem) => {
        setDocuments((prev) => prev.map((doc) => (doc.id === documentId ? updater(doc) : doc)));
    }, []);

    const loadDocuments = useCallback(async (signal?: AbortSignal): Promise<DocumentListItem[] | undefined> => {
        docsPageCache.clear();
        setLoading(true);
        try {
            const res = await documentsApi.getDocuments(page, 20);
            if (signal?.aborted || !isMountedRef.current) return undefined;
            setListFetchError(null);
            setDocuments(res.items);
            const nextHasMore = page < res.pages;
            setHasMore(nextHasMore);
            setCachedDocsPage(page, res.items, nextHasMore);
            const visibleIds = new Set(res.items.map((item) => item.id));
            setStatusSnapshots((prev) => {
                const next: Record<string, PersistedStatusSnapshot> = {};
                Object.entries(prev).forEach(([id, snapshot]) => {
                    if (visibleIds.has(id)) {
                        next[id] = snapshot;
                    }
                });
                return next;
            });
            setPollIssues((prev) => {
                const next: Record<string, PollIssue> = {};
                Object.entries(prev).forEach(([id, issue]) => {
                    if (visibleIds.has(id)) {
                        next[id] = issue;
                    }
                });
                return next;
            });
            return res.items;
        } catch (error) {
            if (signal?.aborted || !isMountedRef.current) return undefined;
            console.error('Failed to load documents', error);
            setListFetchError(humanizeError(getApiErrorMessage(error)));
            return undefined;
        } finally {
            if (!signal?.aborted && isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [page]);

    const applyStatusSnapshot = useCallback((documentId: string, snapshot: PersistedStatusSnapshot) => {
        setStatusSnapshots((prev) => ({
            ...prev,
            [documentId]: snapshot,
        }));
        updateDocument(documentId, (doc) => ({
            ...doc,
            status: snapshot.status,
            total_pages: snapshot.total_pages || doc.total_pages,
            error_message: snapshot.error_message ?? doc.error_message ?? null,
        }));
    }, [updateDocument]);

    const refreshDocumentStatus = useCallback(async (documentId: string, options?: { manual?: boolean }) => {
        if (activePollRequestsRef.current.has(documentId)) {
            return;
        }
        if (options?.manual) {
            suppressedPollsRef.current.delete(documentId);
        }

        activePollRequestsRef.current.add(documentId);
        try {
            const response = await documentsApi.getDocumentStatus(documentId);
            if (!isMountedRef.current) return;

            const normalizedStatus = normalizeDocumentStatus(response.status);
            applyStatusSnapshot(documentId, {
                ...response,
                status: normalizedStatus,
            });
            setPollIssues((prev) => {
                if (!prev[documentId]) return prev;
                const next = { ...prev };
                delete next[documentId];
                return next;
            });
            if (isTerminalStatus(normalizedStatus)) {
                const priorMime = (documentsRef.current.find((d) => d.id === documentId)?.mime_type || '').toLowerCase();
                suppressedPollsRef.current.delete(documentId);
                primedPollsRef.current.delete(documentId);
                const freshItems = await loadDocuments();
                const mime = (freshItems?.find((d) => d.id === documentId)?.mime_type || priorMime).toLowerCase();
                if (mime.startsWith('image/')) {
                    onEntitlementsRefresh?.();
                }
            }
        } catch (error) {
            if (!isMountedRef.current) return;
            const issue = getPollingIssueMessage(error);
            setPollIssues((prev) => ({
                ...prev,
                [documentId]: issue,
            }));
            suppressedPollsRef.current.add(documentId);
        } finally {
            activePollRequestsRef.current.delete(documentId);
        }
    }, [applyStatusSnapshot, loadDocuments, onEntitlementsRefresh]);

    useEffect(() => {
        isMountedRef.current = true;
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const cached = getCachedDocsPage(page);
        if (cached && refreshTrigger === 0) {
            setDocuments(cached.items);
            setHasMore(cached.hasMore);
            const isFresh = Date.now() - cached.savedAt < DOCS_STALE_MS;
            if (isFresh) {
                setListFetchError(null);
                setLoading(false);
                return;
            }
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        loadDocuments(controller.signal);

        return () => {
            isMountedRef.current = false;
            controller.abort();
        };
    }, [loadDocuments, refreshTrigger, page]);

    useEffect(() => {
        const activeIds = documents
            .filter((doc) => {
                const effectiveStatus = statusSnapshots[doc.id]?.status || doc.status;
                return shouldPollStatus(effectiveStatus) && !suppressedPollsRef.current.has(doc.id);
            })
            .map((doc) => doc.id);

        activeIds.forEach((documentId) => {
            if (!primedPollsRef.current.has(documentId)) {
                primedPollsRef.current.add(documentId);
                void refreshDocumentStatus(documentId);
            }
        });

        documents.forEach((doc) => {
            const effectiveStatus = statusSnapshots[doc.id]?.status || doc.status;
            if (!shouldPollStatus(effectiveStatus)) {
                primedPollsRef.current.delete(doc.id);
            }
        });
    }, [documents, refreshDocumentStatus, statusSnapshots]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            documents.forEach((doc) => {
                const effectiveStatus = statusSnapshots[doc.id]?.status || doc.status;
                if (!shouldPollStatus(effectiveStatus)) {
                    return;
                }
                if (suppressedPollsRef.current.has(doc.id)) {
                    return;
                }
                void refreshDocumentStatus(doc.id);
            });
        }, POLL_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [documents, refreshDocumentStatus, statusSnapshots]);

    const handleDownload = async (doc: DocumentListItem) => {
        // Pre-open a tab synchronously to avoid popup blockers.
        const popup = window.open('', '_blank');
        try {
            const { url } = await documentsApi.getDownloadUrl(doc.id);
            if (popup) {
                popup.location.href = url;
            } else {
                window.location.href = url;
            }
        } catch (error) {
            if (popup) {
                popup.close();
            }
            console.error('Download failed', error);
            setToast({ message: humanizeError(getApiErrorMessage(error)), severity: 'error' });
        }
    };

    const handleReuploadClick = (doc: DocumentListItem) => {
        setPendingReuploadDoc(doc);
        fileInputRef.current?.click();
    };

    const handleCreateWorkItem = async (doc: DocumentListItem) => {
        if (creatingWorkItem) return;
        setCreatingWorkItem(true);
        try {
            const result = await documentsApi.processDocument(doc.id, {
                intent: doc.doc_type || 'general',
                hint_domain: getHintDomain(doc.doc_type),
            });
            const workItemId = result.work_item?.id || result.work_items?.[0]?.id;
            await loadDocuments();
            const mime = (doc.mime_type || '').toLowerCase();
            if (mime.startsWith('image/')) {
                onEntitlementsRefresh?.();
            }
            if (workItemId) {
                setToast({ message: 'Task created! Taking you there now.', severity: 'success' });
                navigate(`/app/today?focusId=${workItemId}`);
            } else {
                setToast({
                    message:
                        'No automatic task was created. You can use “Create Task” again if you want one in your queue—this is optional recovery, not an error.',
                    severity: 'info',
                });
            }
        } catch (error) {
            console.error('Create work item failed', error);
            setToast({ message: humanizeError(getApiErrorMessage(error)), severity: 'error' });
        } finally {
            setCreatingWorkItem(false);
        }
    };

    const handleCreateDraftAnyway = async (doc: DocumentListItem) => {
        if (creatingWorkItem) return;
        setCreatingWorkItem(true);
        try {
            const domain = getHintDomain(doc.doc_type) || 'general';
            const created = await workItemsApi.createWorkItem({
                title: doc.filename || 'Untitled Document',
                type: 'INBOX',
                state: 'TRIAGE',
                needs_action: true,
                domain,
                document_id: doc.id,
                summary: 'Created from a document upload that needs review.',
                meta: {
                    source: 'document',
                    needs_review: true,
                    intake_recovery_draft: true,
                },
            });
            await loadDocuments();
            setToast({ message: 'Draft created. Taking you there now.', severity: 'success' });
            navigate(`/app/today?focusId=${created.id}`);
        } catch (error) {
            console.error('Create draft failed', error);
            setToast({ message: humanizeError(getApiErrorMessage(error)), severity: 'error' });
        } finally {
            setCreatingWorkItem(false);
        }
    };

    const handleReuploadFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !pendingReuploadDoc) return;

        if (!supabase) {
            setToast({ message: 'Storage is not configured. Please contact support.', severity: 'error' });
            return;
        }

        const validation = validateFile(file);
        if (!validation.valid) {
            setToast({ message: validation.friendlyError || validation.error || 'Invalid file', severity: 'error' });
            return;
        }

        setReuploading(true);
        try {
            const doc = await documentsApi.getDocument(pendingReuploadDoc.id);
            const { error: uploadError } = await supabase.storage
                .from('documents')
                .upload(doc.storage_path, file, {
                    contentType: file.type,
                    upsert: true,
                });

            if (uploadError) throw uploadError;

            await documentsApi.processDocument(doc.id, {
                intent: doc.doc_type || 'general',
                hint_domain: getHintDomain(doc.doc_type),
            });
            await loadDocuments();
            const reMime = (pendingReuploadDoc.mime_type || '').toLowerCase();
            if (reMime.startsWith('image/')) {
                onEntitlementsRefresh?.();
            }
            setToast({ message: 'New file uploaded! Analyzing now.', severity: 'success' });
        } catch (error) {
            console.error('Re-upload failed', error);
            setToast({ message: humanizeError(getApiErrorMessage(error)), severity: 'error' });
        } finally {
            setReuploading(false);
            setPendingReuploadDoc(null);
        }
    };

    const startPollingAwareProcess = useCallback(async (
        doc: DocumentListItem,
        successMessage: string,
        options?: { mode?: string },
    ) => {
        const response = await documentsApi.processDocument(doc.id, {
            ...getActionPayload(doc),
            ...(options?.mode ? { mode: options.mode } : {}),
        });
        if ((response as any).async) {
            suppressedPollsRef.current.delete(doc.id);
            setPollIssues((prev) => {
                if (!prev[doc.id]) return prev;
                const next = { ...prev };
                delete next[doc.id];
                return next;
            });
            applyStatusSnapshot(doc.id, {
                status: 'processing',
                total_pages: Number((response as any).total_pages ?? doc.total_pages ?? 1),
                pages_completed: 0,
                pages_failed: 0,
                error_message: null,
            });
            await loadDocuments();
            void refreshDocumentStatus(doc.id, { manual: true });
            setToast({ message: successMessage, severity: 'info' });
            return;
        }

        const nextDocument = response.document;
        if (nextDocument) {
            applyStatusSnapshot(doc.id, {
                status: normalizeDocumentStatus(nextDocument.status),
                total_pages: nextDocument.total_pages ?? doc.total_pages ?? 1,
                pages_completed:
                    normalizeDocumentStatus(nextDocument.status) === 'processed'
                        ? nextDocument.total_pages ?? doc.total_pages ?? 1
                        : 0,
                pages_failed: 0,
                error_message: nextDocument.error_message ?? null,
            });
        }
        if (
            options?.mode === 'analytical' &&
            nextDocument &&
            normalizeDocumentStatus(nextDocument.status) === 'processed' &&
            nextDocument.mode !== 'analytical'
        ) {
            await loadDocuments();
            const mimeEarly = (doc.mime_type || '').toLowerCase();
            if (mimeEarly.startsWith('image/')) {
                onEntitlementsRefresh?.();
            }
            setToast({
                message:
                    'We could not confirm deep analysis from the server. Refresh the list or try "Run deep analysis" again.',
                severity: 'info',
            });
            return;
        }
        const primaryId = response.work_item?.id || response.work_items?.[0]?.id;
        await loadDocuments();
        const mime = (doc.mime_type || '').toLowerCase();
        if (mime.startsWith('image/')) {
            onEntitlementsRefresh?.();
        }
        setToast({
            message:
                normalizeDocumentStatus(nextDocument?.status) === 'processed'
                    ? primaryId
                        ? 'Analysis finished successfully.'
                        : 'Analysis finished. No automatic task was created—you can add one from this row if helpful (optional).'
                    : successMessage,
            severity: 'success',
        });
    }, [applyStatusSnapshot, loadDocuments, refreshDocumentStatus, onEntitlementsRefresh]);

    const handleRetryAnalysis = useCallback(async (doc: DocumentListItem) => {
        try {
            await startPollingAwareProcess(doc, 'Retry started. We will keep checking this document below.');
        } catch (error) {
            setToast({ message: getActionErrorMessage(error), severity: 'error' });
        }
    }, [startPollingAwareProcess]);

    const handleRetryFailedPages = useCallback(async (doc: DocumentListItem) => {
        try {
            const pages = await documentsApi.getDocumentPages(doc.id);
            const failedPageNumbers = pages
                .filter((page: DocumentPageInfo) => page.status === 'failed')
                .map((page: DocumentPageInfo) => page.page_number);

            if (failedPageNumbers.length === 0) {
                setToast({
                    message: 'No failed pages are available to retry right now. Try refreshing the document status.',
                    severity: 'info',
                });
                return;
            }

            await documentsApi.reprocessDocument(doc.id, failedPageNumbers);
            await startPollingAwareProcess(doc, 'Retrying the failed pages. We will keep checking this document below.');
        } catch (error) {
            setToast({ message: getActionErrorMessage(error), severity: 'error' });
        }
    }, [startPollingAwareProcess]);

    const statusDetails = useMemo(() => {
        return documents.reduce<Record<string, { message: string; canRefresh: boolean }>>((acc, doc) => {
            const snapshot = statusSnapshots[doc.id];
            const issue = pollIssues[doc.id];
            const normalizedStatus = normalizeDocumentStatus(snapshot?.status || doc.status);
            const errorMessage = snapshot?.error_message ?? doc.error_message;

            if (issue) {
                acc[doc.id] = {
                    message: issue.message,
                    canRefresh: issue.kind === 'transient',
                };
                return acc;
            }

            if (normalizedStatus === 'uploaded') {
                acc[doc.id] = {
                    message: 'Upload complete. Analysis will start shortly.',
                    canRefresh: false,
                };
                return acc;
            }

            if (normalizedStatus === 'processing') {
                const totalPages = snapshot?.total_pages || doc.total_pages || 1;
                const pagesCompleted = snapshot?.pages_completed ?? 0;
                const pagesFailed = snapshot?.pages_failed ?? 0;
                const pagesTouched = Math.min(totalPages, pagesCompleted + pagesFailed);
                acc[doc.id] = {
                    message:
                        totalPages > 1 && pagesTouched > 0
                            ? `${pagesTouched} of ${totalPages} pages checked${pagesFailed > 0 ? `, ${pagesFailed} need another try` : ''}.`
                            : 'Analysis is underway. Larger documents can take a bit longer.',
                    canRefresh: false,
                };
                return acc;
            }

            if (normalizedStatus === 'partial') {
                const pagesFailed = snapshot?.pages_failed ?? 0;
                const noTask = !doc.work_item_id;
                let message: string;
                if (noTask) {
                    message =
                        pagesFailed > 0
                            ? `${pagesFailed} page${pagesFailed === 1 ? '' : 's'} still need another try. If you want something in your task list, “Retry failed pages” or “Create a follow-up task anyway” are optional recovery—not something you must fix.`
                            : 'Part of this document may still need attention, and no automatic task was linked. Retry or a follow-up task are only if you want to track follow-up.';
                } else if (pagesFailed > 0) {
                    message = `${pagesFailed} page${pagesFailed === 1 ? '' : 's'} still need another try.`;
                } else {
                    message = 'Some of this document is ready, but part of it still needs attention.';
                }
                acc[doc.id] = {
                    message,
                    canRefresh: false,
                };
                return acc;
            }

            if (normalizedStatus === 'failed') {
                acc[doc.id] = {
                    message: errorMessage
                        ? `${humanizeError(errorMessage)} Your file is still saved. Retry, replace, or “Create a follow-up task anyway” are optional recovery if you want a task to track follow-up.`
                        : 'We saved your file, but analysis did not finish this time. Retry or a follow-up task are optional—you can also leave the file here.',
                    canRefresh: false,
                };
                return acc;
            }

            if (normalizedStatus === 'unknown') {
                acc[doc.id] = {
                    message: 'We received an unexpected status for this document. Try refreshing the page or retrying manually.',
                    canRefresh: true,
                };
                return acc;
            }

            if (normalizedStatus === 'processed' && !doc.work_item_id) {
                acc[doc.id] = {
                    message:
                        'Analysis finished without an automatic task. “Create Task” is optional if you want an item in your queue—it does not mean anything went wrong.',
                    canRefresh: false,
                };
                return acc;
            }

            acc[doc.id] = {
                message: doc.work_item_id ? 'Analysis finished and your task is ready.' : 'Analysis finished.',
                canRefresh: false,
            };
            return acc;
        }, {});
    }, [documents, pollIssues, statusSnapshots]);

    const handleDeleteClick = (doc: DocumentListItem) => {
        setPendingDeleteDoc(doc);
        setDeleteDialogOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setPendingDeleteDoc(null);
    };

    const handleDeleteConfirm = async () => {
        if (!pendingDeleteDoc) return;

        setDeleting(true);
        try {
            await documentsApi.deleteDocument(pendingDeleteDoc.id);
            setDocuments(prev => prev.filter(d => d.id !== pendingDeleteDoc.id));
            setDeleteDialogOpen(false);
            setPendingDeleteDoc(null);
            setToast({ message: 'Document deleted.', severity: 'info' });
        } catch (error) {
            console.error('Delete failed', error);
            setToast({ message: humanizeError(getApiErrorMessage(error)), severity: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    if (loading && documents.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={24} sx={{ color: 'var(--color-text-muted)' }} />
            </Box>
        );
    }

    if (!loading && documents.length === 0 && listFetchError) {
        return (
            <Box sx={{ py: 4, px: 1 }}>
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={() => void loadDocuments()}>
                            Retry
                        </Button>
                    )}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{DECISION_GUIDE_LIST_FAILED_TITLE}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{DECISION_GUIDE_LIST_FAILED_BODY}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>{listFetchError}</Typography>
                </Alert>
            </Box>
        );
    }

    if (documents.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 6, color: 'var(--color-text-muted)' }}>
                <Typography variant="body2">No documents uploaded yet. Upload your first document above!</Typography>
            </Box>
        );
    }

    return (
        <>
            {listFetchError && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={(
                        <Button color="inherit" size="small" onClick={() => void loadDocuments()}>
                            Retry
                        </Button>
                    )}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{DECISION_GUIDE_LIST_FAILED_TITLE}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{DECISION_GUIDE_LIST_FAILED_BODY}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>{listFetchError}</Typography>
                </Alert>
            )}
            {/* Card-based layout instead of table - much better for responsive */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {documents.map((doc) => {
                    const normalizedStatus = normalizeDocumentStatus(statusSnapshots[doc.id]?.status || doc.status);
                    return (
                    <Paper
                        key={doc.id}
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: 'var(--color-bg-card)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 2,
                            width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Main row: file info + status + actions */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: 2,
                            flexWrap: 'wrap',
                        }}>
                            {/* File info */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '1 1 200px', minWidth: 0 }}>
                                <Box sx={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {getFileIcon(doc.mime_type)}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontWeight: 500, 
                                            color: 'var(--color-text-primary)',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={doc.filename}
                                    >
                                        {doc.filename}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                        {formatFileSize(doc.size_bytes)}
                                        {(doc as any).total_pages > 1 ? ` • ${(doc as any).total_pages} pages` : ''}
                                        {' • '}{new Date(doc.created_at || doc.processed_at || Date.now()).toLocaleDateString()}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Status badges */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', flex: '0 0 auto' }}>
                                <StatusBadge status={normalizedStatus} error={doc.error_message} />
                                {normalizedStatus === 'processed' && doc.requires_human_review && (
                                    <Chip
                                        size="small"
                                        label="Review required"
                                        icon={<FontAwesomeIcon icon={faCircleExclamation} />}
                                        sx={{
                                            height: 22,
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            color: 'var(--color-text-secondary)',
                                            bgcolor: 'rgba(148, 163, 184, 0.12)',
                                            border: '1px solid rgba(148, 163, 184, 0.35)',
                                            '& .MuiChip-icon': { fontSize: '0.6rem', color: 'inherit' }
                                        }}
                                    />
                                )}
                                {doc.has_guidance && normalizedStatus === 'processed' && !loading && (
                                    <Chip
                                        size="small"
                                        label="Guidance"
                                        icon={<FontAwesomeIcon icon={faShieldHalved} />}
                                        sx={{
                                            height: 22,
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            color: 'var(--color-accent)',
                                            bgcolor: 'rgba(99, 102, 241, 0.1)',
                                            border: '1px solid rgba(99, 102, 241, 0.2)',
                                            '& .MuiChip-icon': { fontSize: '0.6rem', color: 'inherit' }
                                        }}
                                    />
                                )}
                                {doc.scam_risk_level && normalizedStatus === 'processed' && (
                                    <Chip
                                        size="small"
                                        label={`Risk: ${doc.scam_risk_level}`}
                                        icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                                        sx={{
                                            height: 22,
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            color: doc.scam_risk_level === 'high' ? '#ef4444' : doc.scam_risk_level === 'medium' ? '#f59e0b' : 'var(--color-text-muted)',
                                            bgcolor: doc.scam_risk_level === 'high' ? 'rgba(239, 68, 68, 0.08)' : doc.scam_risk_level === 'medium' ? 'rgba(245, 158, 11, 0.08)' : 'rgba(148, 163, 184, 0.08)',
                                            border: '1px solid rgba(148, 163, 184, 0.2)',
                                            '& .MuiChip-icon': { fontSize: '0.6rem', color: 'inherit' }
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: '0 0 auto', flexWrap: 'wrap' }}>
                                {normalizedStatus === 'failed' && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => void handleRetryAnalysis(doc)}
                                        disabled={deleting || reuploading}
                                        sx={{
                                            fontSize: '0.7rem',
                                            textTransform: 'none',
                                            borderColor: 'var(--color-accent)',
                                            color: 'var(--color-accent)',
                                            px: 1.5,
                                            minWidth: 'auto',
                                            '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.05)', borderColor: 'var(--color-accent)' }
                                        }}
                                        startIcon={<FontAwesomeIcon icon={faRotateRight} style={{ fontSize: '0.65rem' }} />}
                                    >
                                        Retry analysis
                                    </Button>
                                )}
                                {normalizedStatus === 'partial' && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => void handleRetryFailedPages(doc)}
                                        disabled={deleting || reuploading}
                                        sx={{
                                            fontSize: '0.7rem',
                                            textTransform: 'none',
                                            borderColor: 'var(--color-accent)',
                                            color: 'var(--color-accent)',
                                            px: 1.5,
                                            minWidth: 'auto',
                                            '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.05)', borderColor: 'var(--color-accent)' }
                                        }}
                                        startIcon={<FontAwesomeIcon icon={faRotateRight} style={{ fontSize: '0.65rem' }} />}
                                    >
                                        Retry failed pages
                                    </Button>
                                )}
                                {normalizedStatus === 'failed' && (
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => handleReuploadClick(doc)}
                                        disabled={deleting || reuploading}
                                        sx={{
                                            fontSize: '0.7rem',
                                            textTransform: 'none',
                                            color: 'var(--color-text-secondary)',
                                            px: 1,
                                            minWidth: 'auto',
                                        }}
                                        startIcon={<FontAwesomeIcon icon={faUpload} style={{ fontSize: '0.65rem' }} />}
                                    >
                                        Replace
                                    </Button>
                                )}
                                {normalizedStatus === 'failed' && !doc.work_item_id && (
                                    <Tooltip title="Optional recovery — adds a follow-up task if you want to track next steps.">
                                        <span>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleCreateDraftAnyway(doc)}
                                                disabled={deleting || reuploading || creatingWorkItem}
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    textTransform: 'none',
                                                    borderColor: 'var(--color-text-muted)',
                                                    color: 'var(--color-text-secondary)',
                                                    px: 1.5,
                                                    minWidth: 'auto',
                                                    '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.08)', borderColor: 'var(--color-text-muted)' }
                                                }}
                                                startIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.65rem' }} />}
                                            >
                                                Create a follow-up task anyway
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                {normalizedStatus === 'processed' && !doc.work_item_id && (
                                    <Tooltip title="Optional recovery — only if you want a task in your queue; no automatic task does not mean failure.">
                                        <span>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleCreateWorkItem(doc)}
                                                disabled={deleting || reuploading || creatingWorkItem}
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    textTransform: 'none',
                                                    borderColor: 'var(--color-accent)',
                                                    color: 'var(--color-accent)',
                                                    px: 1.5,
                                                    minWidth: 'auto',
                                                    '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.05)', borderColor: 'var(--color-accent)' }
                                                }}
                                                startIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.65rem' }} />}
                                            >
                                                Create Task
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                {doc.work_item_id && (doc.work_items_count ?? 0) > 1 && (
                                    <Chip
                                        size="small"
                                        label={`${doc.work_items_count} tasks from this document`}
                                        sx={{
                                            height: 22,
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            color: 'var(--color-text-secondary)',
                                            bgcolor: 'rgba(99, 102, 241, 0.08)',
                                            border: '1px solid rgba(99, 102, 241, 0.2)',
                                        }}
                                    />
                                )}
                                {doc.work_item_id && (
                                    <>
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => navigate(`/app/today?focusId=${doc.work_item_id}`)}
                                            sx={{
                                                fontSize: '0.7rem',
                                                textTransform: 'none',
                                                color: 'var(--color-accent)',
                                                px: 1,
                                                minWidth: 'auto',
                                            }}
                                            endIcon={<FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.6rem' }} />}
                                        >
                                            View primary task
                                        </Button>
                                        {(doc.work_items_count ?? 0) > 1 && (
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => navigate('/app/workbench')}
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    textTransform: 'none',
                                                    color: 'var(--color-text-secondary)',
                                                    px: 1,
                                                    minWidth: 'auto',
                                                }}
                                            >
                                                Open workbench
                                            </Button>
                                        )}
                                    </>
                                )}
                                <Tooltip title="Download">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDownload(doc)}
                                        sx={{ color: 'var(--color-text-secondary)', p: 0.75 }}
                                        disabled={deleting}
                                    >
                                        <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.85rem' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteClick(doc)}
                                        disabled={deleting}
                                        sx={{
                                            color: 'var(--color-text-muted)',
                                            p: 0.75,
                                            '&:hover': { color: '#ef4444' },
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.85rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Error message for failed docs - reassuring tone */}
                        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(148, 163, 184, 0.06)', border: '1px solid rgba(148, 163, 184, 0.16)' }}>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', display: 'block' }}>
                                {statusDetails[doc.id]?.message}
                            </Typography>
                            {statusDetails[doc.id]?.canRefresh && (
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => void refreshDocumentStatus(doc.id, { manual: true })}
                                    sx={{
                                        mt: 0.5,
                                        px: 0,
                                        minWidth: 'auto',
                                        fontSize: '0.72rem',
                                        textTransform: 'none',
                                    }}
                                >
                                    Refresh status
                                </Button>
                            )}
                        </Box>

                        {/* Guidance Card - Prominent display for high-anxiety docs */}
                        {(normalizedStatus === 'processed' ||
                            normalizedStatus === 'partial' ||
                            (normalizedStatus === 'processing' && doc.mode === 'analytical')) && (
                            <DocumentsAnalyticalPanel
                                doc={doc}
                                analyticalEnabled={analyticalEnabled}
                                normalizedStatus={normalizedStatus}
                                onToast={(message, severity) => setToast({ message, severity })}
                                onRunDeepAnalysis={async (d) => {
                                    try {
                                        await startPollingAwareProcess(
                                            d,
                                            'Deep analysis started. We will keep checking this document below.',
                                            { mode: 'analytical' },
                                        );
                                    } catch (error) {
                                        setToast({ message: getActionErrorMessage(error), severity: 'error' });
                                    }
                                }}
                                onRefreshAfterAsk={() => void loadDocuments()}
                            />
                        )}

                        {normalizedStatus === 'processed' && doc.has_guidance && (
                            <IntakeDecisionGuideCard
                                state={
                                    loading ? 'loading'
                                        : listFetchError ? 'error'
                                            : 'ready'
                                }
                                calmSummary={doc.calm_summary}
                                safeFirstStep={doc.safe_first_step}
                                workItemId={doc.work_item_id}
                                onReviewOptions={
                                    doc.work_item_id
                                        ? () => navigate(`/app/today?focusId=${doc.work_item_id}`)
                                        : undefined
                                }
                                onRetry={() => void loadDocuments()}
                            />
                        )}
                    </Paper>
                    );
                })}
            </Box>

            {/* Pagination */}
            {(hasMore || page > 1) && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <Button size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                        Previous
                    </Button>
                    <Button size="small" disabled={!hasMore} onClick={() => setPage(p => p + 1)}>
                        Next
                    </Button>
                </Box>
            )}

            <input
                type="file"
                hidden
                ref={fileInputRef}
                accept="application/pdf,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={handleReuploadFileChange}
            />

            <ConfirmationDialog
                open={deleteDialogOpen}
                title="Delete Document"
                message={pendingDeleteDoc
                    ? `Are you sure you want to delete "${pendingDeleteDoc.filename}"? This can't be undone.`
                    : 'Are you sure you want to delete this document?'
                }
                confirmLabel={deleting ? 'Deleting...' : 'Delete'}
                cancelLabel="Keep It"
                isDestructive={true}
                isLoading={deleting}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />

            <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}>
                <Alert severity={toast?.severity || 'info'} sx={{ width: '100%' }}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </>
    );
};

// Softened status badge - "Needs Attention" instead of scary "Failed"
const StatusBadge = ({ status, error }: { status: string, error?: string | null }) => {
    const normalizedStatus = normalizeDocumentStatus(status);
    let color = 'var(--color-text-muted)';
    let bgcolor = 'rgba(0,0,0,0.05)';
    let label: string = normalizedStatus;
    let icon = <FontAwesomeIcon icon={faUpload} />;

    if (normalizedStatus === 'processed') {
        color = '#10b981';
        bgcolor = 'rgba(16, 185, 129, 0.1)';
        label = 'Ready';
        icon = <FontAwesomeIcon icon={faCheckCircle} />;
    } else if (normalizedStatus === 'partial') {
        color = '#f59e0b';
        bgcolor = 'rgba(245, 158, 11, 0.1)';
        label = 'Partial';
        icon = <FontAwesomeIcon icon={faCircleExclamation} />;
    } else if (normalizedStatus === 'processing') {
        color = '#3b82f6';
        bgcolor = 'rgba(59, 130, 246, 0.1)';
        label = 'Analyzing';
        icon = <FontAwesomeIcon icon={faRotateRight} spin />;
    } else if (normalizedStatus === 'failed') {
        color = '#f59e0b';
        bgcolor = 'rgba(245, 158, 11, 0.1)';
        label = 'Needs Attention';
        icon = <FontAwesomeIcon icon={faCircleExclamation} />;
    } else if (normalizedStatus === 'uploaded') {
        color = '#6b7280';
        bgcolor = 'rgba(107, 114, 128, 0.1)';
        label = 'Uploaded';
        icon = <FontAwesomeIcon icon={faUpload} />;
    } else if (normalizedStatus === 'unknown') {
        color = '#f59e0b';
        bgcolor = 'rgba(245, 158, 11, 0.1)';
        label = 'Status Unknown';
        icon = <FontAwesomeIcon icon={faCircleExclamation} />;
    }

    return (
        <Tooltip title={error ? 'We can help fix this' : ''}>
            <Chip
                size="small"
                label={label}
                icon={icon || undefined}
                sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: color,
                    bgcolor: bgcolor,
                    border: '1px solid',
                    borderColor: normalizedStatus === 'failed' ? 'rgba(245, 158, 11, 0.3)' : 'transparent',
                    '& .MuiChip-icon': { color: 'inherit', fontSize: '0.65rem' }
                }}
            />
        </Tooltip>
    );
};

export default DocumentsHistory;
